'use client';

import {
  RosterFilterData,
  RosterFilterDialog as RosterFilterDialogUI,
  RosterFilterProps,
} from '@wwe-portal/ui/features/roster';

import { ChangeEvent, useMemo, useState } from 'react';

import debounce from 'lodash.debounce';
import {
  createPortableDialog,
  usePortableDialog,
} from '@wwe-portal/ui/components/dialog/portable-dialog';
import { EmptySearchResult } from '@wwe-portal/ui/components/data-display/empty-search-result';
import { useTranslations } from 'next-intl';

import { FilterablePlayerRoster } from '../type';

type keyFilterablePlayerRoster = keyof FilterablePlayerRoster;
type keyRosterFilterData = keyof RosterFilterData;
const defaultFilterValue = {
  era: null,
  class: null,
  stable: null,
};

const isMatch = (
  value: string | number | boolean | string[] | null | undefined,
  searchText: string | null
): boolean => {
  if (!searchText) return true;
  if (value == null) return false;
  if (typeof value === 'string') {
    return value.toLowerCase().includes(searchText?.toLowerCase());
  }
  if (Array.isArray(value)) {
    return value.some((item) => isMatch(item, searchText));
  }
  return true;
};

export const RosterFilterDialog = createPortableDialog(
  ({
    title,
    hidden,
    playerRosters,
    onClickRoster,
  }: {
    title: string;
    hidden: boolean;
    playerRosters: FilterablePlayerRoster[];
    onClickRoster: (rosterId: string) => void;
  }) => {
    const modal = usePortableDialog();
    const t = useTranslations();
    const [searchText, setSearchText] = useState<string>('');
    const [inputText, setInputText] = useState<string>('');
    const [filterValue, setFilterValue] =
      useState<RosterFilterData>(defaultFilterValue);

    const onClose = () => {
      setFilterValue(defaultFilterValue);
      setInputText('');
      modal.remove();
    };

    const debounceChangeSearchText = useMemo(
      () => debounce((value: string) => setSearchText(value), 300),
      []
    );

    const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
      debounceChangeSearchText(e.target.value);
      setInputText(e.target.value);
    };

    const getFilterDataAction = (type: 'class' | 'era' | 'stable') => {
      return {
        onClearFilter: () =>
          setFilterValue((value) => ({ ...value, [type]: null })),
        onSelectItem: (id: string) =>
          setFilterValue((value) => ({
            ...value,
            [type]: value[type] === id ? null : id,
          })),
      };
    };

    const filterData: RosterFilterProps['filterData'] = {
      class: {
        title: 'Class',
        items: [
          {
            id: 'Yellow',
            title: 'showboats',
            iconUrl: '/assets/images/superstar/showboats.webp',
          },
          {
            id: 'Blue',
            title: 'acrobats',
            iconUrl: '/assets/images/superstar/acrobats.webp',
          },
          {
            id: 'Red',
            title: 'powerhouses',
            iconUrl: '/assets/images/superstar/powerhouses.webp',
          },
          {
            id: 'Green',
            title: 'technicians',
            iconUrl: '/assets/images/superstar/technicians.webp',
          },
          {
            id: 'Purple',
            title: 'tricksters',
            iconUrl: '/assets/images/superstar/tricksters.webp',
          },
          {
            id: 'Black',
            title: 'strikers',
            iconUrl: '/assets/images/superstar/strikers.webp',
          },
        ],
        clearFilterText: 'All Classes',
        ...getFilterDataAction('class'),
      },
      era: {
        title: 'Era',
        items: [
          {
            id: 'Attitude',
            title: 'attitude',
            iconUrl: '/assets/images/superstar/attitude.webp',
          },
          {
            id: 'Classic',
            title: 'legends',
            iconUrl: '/assets/images/superstar/legends.webp',
          },
          {
            id: 'Modern',
            title: 'modern',
            iconUrl: '/assets/images/superstar/modern.webp',
          },
          {
            id: 'NewGen',
            title: 'New Generation',
            iconUrl: '/assets/images/superstar/newGeneration.webp',
          },
          {
            id: 'Reality',
            title: 'reality',
            iconUrl: '/assets/images/superstar/reality.webp',
          },
          {
            id: 'PG',
            title: 'pg',
            iconUrl: '/assets/images/superstar/PG.webp',
          },
          {
            id: 'RuthlessAggression',
            title: 'ruthless_aggression',
            iconUrl: '/assets/images/superstar/ruthlessAggression.webp',
          },
          {
            id: 'HallofFame',
            title: 'hall.Of.fame',
            iconUrl: '/assets/images/superstar/hallOfFame.webp',
          },
        ],
        clearFilterText: 'All Eras',
        ...getFilterDataAction('era'),
      },
      stable: {
        title: 'Stable',
        items: [
          {
            id: 'Stable_Alliance',
            title: 'alliance',
            iconUrl: '/assets/images/superstar/Alliance.webp',
          },
          {
            id: 'Stable_Authority',
            title: 'authority',
            iconUrl: '/assets/images/superstar/Authority.webp',
          },
          {
            id: 'Stable_Club',
            title: 'club',
            iconUrl: '/assets/images/superstar/Club.webp',
          },
          {
            id: 'Stable_CORP',
            title: 'corp',
            iconUrl: '/assets/images/superstar/CORP.webp',
          },
          {
            id: 'Stable_DiamondExchange',
            title: 'diamond.exchange',
            iconUrl: '/assets/images/superstar/DiamondExchange.webp',
          },
          {
            id: 'Stable_DX',
            title: 'dx',
            iconUrl: '/assets/images/superstar/DX.webp',
          },
          {
            id: 'Stable_Evolution',
            title: 'evolution',
            iconUrl: '/assets/images/superstar/Evolution.webp',
          },
          {
            id: 'Stable_FourHorsemen',
            title: 'four.horsemen',
            iconUrl: '/assets/images/superstar/FourHorsemen.webp',
          },
          {
            id: 'Stable_HartFoundation',
            title: 'hart.foundation',
            iconUrl: '/assets/images/superstar/HartFoundation.webp',
          },
          {
            id: 'Stable_HeenanFamily',
            title: 'heenan.family',
            iconUrl: '/assets/images/superstar/HeenanFamily.png',
          },
          {
            id: 'Stable_LeagueOfNations',
            title: 'league.of.nations',
            iconUrl: '/assets/images/superstar/LeagueOfNations.webp',
          },
          {
            id: 'Stable_Legacy',
            title: 'legacy',
            iconUrl: '/assets/images/superstar/Legacy.webp',
          },
          {
            id: 'Stable_MillionairesClub',
            title: 'millionaires.club',
            iconUrl: '/assets/images/superstar/MillionairesClub.webp',
          },
          {
            id: 'Stable_Nexus',
            title: 'nexus',
            iconUrl: '/assets/images/superstar/Nexus.png',
          },
          {
            id: 'Stable_NOD',
            title: 'nod',
            iconUrl: '/assets/images/superstar/NOD.png',
          },
          {
            id: 'Stable_NWO',
            title: 'nwo',
            iconUrl: '/assets/images/superstar/NWO.webp',
          },
          {
            id: 'Stable_RatPack',
            title: 'rat.pack',
            iconUrl: '/assets/images/superstar/RatPack.png',
          },
          {
            id: 'Stable_Shield',
            title: 'shield',
            iconUrl: '/assets/images/superstar/Shield.webp',
          },
          {
            id: 'Stable_Z',
            title: 'z',
            iconUrl: '/assets/images/superstar/Z.webp',
          },
        ],
        clearFilterText: 'All Stables',
        ...getFilterDataAction('stable'),
      },
    };
    const rosters = useMemo(() => {
      return (
        playerRosters
          ?.filter((item) => {
            return Object.keys(filterValue).every((key) => {
              return isMatch(
                item[key as keyFilterablePlayerRoster],
                filterValue?.[key as keyRosterFilterData]
              );
            });
          })
          .filter((item) => !searchText || isMatch(item.name, searchText)) ?? []
      );
    }, [filterValue, searchText, playerRosters]);

    return (
      <RosterFilterDialogUI
        open={modal.visible}
        onClose={onClose}
        hidden={hidden}
        filterProps={{
          title,
          rosters,
          filterData,
          filterValue,
          searchText: inputText,
          onClickRoster,
          onChangeSearch,
          noItemText: (
            <EmptySearchResult>
              {t('header_search_no_result_without_text')}
            </EmptySearchResult>
          ),
        }}
      />
    );
  }
);
